import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiMail, FiPhone, FiCalendar, FiFilter, FiX, FiRefreshCw, FiUser, FiMessageSquare, FiCheckCircle, FiClock, FiAlertCircle } from 'react-icons/fi';
import axios from 'axios';

const API_URL = 'https://fazyra.com';

const Container = styled.div`
  padding: 30px;
  background: #f8f9fa;
  min-height: calc(100vh - 80px);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  h1 {
    font-size: 28px;
    color: #333;
    margin: 0;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => props.color || '#3498db'};
  
  .stat-label {
    font-size: 13px;
    color: #999;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .stat-value {
    font-size: 32px;
    font-weight: 700;
    color: #333;
  }
`;

const FilterBar = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  align-items: center;
`;

const Select = styled.select`
  padding: 10px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  min-width: 150px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const SearchInput = styled.input`
  padding: 10px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  flex: 1;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  background: ${props => props.primary ? '#3498db' : 'white'};
  color: ${props => props.primary ? 'white' : '#666'};
  border: 1px solid ${props => props.primary ? '#3498db' : '#e0e0e0'};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.primary ? '#2980b9' : '#f8f9fa'};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background: #f8f9fa;
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #666;
  font-size: 13px;
  text-transform: uppercase;
  border-bottom: 2px solid #e0e0e0;
`;

const Td = styled.td`
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #333;
`;

const Tr = styled.tr`
  &:hover {
    background: #f8f9fa;
    cursor: pointer;
  }
  
  &:last-child td {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  
  ${props => {
    switch(props.status) {
      case 'new':
        return `
          background: #e3f2fd;
          color: #1976d2;
        `;
      case 'contacted':
        return `
          background: #fff3e0;
          color: #f57c00;
        `;
      case 'in-progress':
        return `
          background: #f3e5f5;
          color: #7b1fa2;
        `;
      case 'converted':
        return `
          background: #e8f5e9;
          color: #2e7d32;
        `;
      case 'closed':
        return `
          background: #fafafa;
          color: #616161;
        `;
      default:
        return `
          background: #f0f0f0;
          color: #666;
        `;
    }
  }}
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    margin: 0;
    color: #333;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  
  &:hover {
    color: #333;
  }
`;

const DetailSection = styled.div`
  margin-bottom: 20px;
  
  h3 {
    font-size: 16px;
    color: #666;
    margin-bottom: 10px;
    border-bottom: 2px solid #f0f0f0;
    padding-bottom: 8px;
  }
`;

const DetailRow = styled.div`
  display: flex;
  padding: 10px 0;
  
  .label {
    font-weight: 600;
    width: 180px;
    color: #666;
  }
  
  .value {
    flex: 1;
    color: #333;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #999;
  
  svg {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  h3 {
    color: #666;
    margin-bottom: 8px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  
  svg {
    animation: spin 1s linear infinite;
    font-size: 32px;
    color: #3498db;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EnquiryManagement = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    fetchEnquiries();
    fetchStats();
  }, [filterStatus]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      console.log('Token being used:', token ? 'Token exists' : 'No token');
      console.log('API URL:', `${API_URL}/api/enquiry/all`);
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      
      const response = await axios.get(`${API_URL}/api/enquiry/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        setEnquiries(response.data.enquiries);
      }
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      alert('Failed to fetch enquiries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/enquiry/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const openEnquiryDetails = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setNotes(enquiry.notes || '');
    setNewStatus(enquiry.status);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEnquiry(null);
    setNotes('');
    setNewStatus('');
  };

  const updateEnquiry = async () => {
    try {
      setUpdateLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await axios.put(
        `${API_URL}/api/enquiry/${selectedEnquiry._id}/status`,
        {
          status: newStatus,
          notes: notes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        alert('✅ Enquiry updated successfully!');
        closeModal();
        fetchEnquiries();
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating enquiry:', error);
      alert('Failed to update enquiry');
    } finally {
      setUpdateLoading(false);
    }
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`${API_URL}/api/enquiry/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        alert('✅ Enquiry deleted successfully!');
        closeModal();
        fetchEnquiries();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      alert('Failed to delete enquiry');
    }
  };

  const filteredEnquiries = enquiries.filter(enquiry => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      enquiry.firstName?.toLowerCase().includes(search) ||
      enquiry.lastName?.toLowerCase().includes(search) ||
      enquiry.email?.toLowerCase().includes(search) ||
      enquiry.phone?.toLowerCase().includes(search) ||
      enquiry.eventType?.toLowerCase().includes(search)
    );
  });

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'new': return <FiAlertCircle />;
      case 'contacted': return <FiPhone />;
      case 'in-progress': return <FiClock />;
      case 'converted': return <FiCheckCircle />;
      case 'closed': return <FiX />;
      default: return <FiAlertCircle />;
    }
  };

  return (
    <Container>
      <Header>
        <h1>📧 Enquiry Management</h1>
        <Button primary onClick={fetchEnquiries}>
          <FiRefreshCw />
          Refresh
        </Button>
      </Header>

      {stats && (
        <Stats>
          <StatCard color="#3498db">
            <div className="stat-label">
              <FiMail /> Total Enquiries
            </div>
            <div className="stat-value">{stats.total}</div>
          </StatCard>
          <StatCard color="#1976d2">
            <div className="stat-label">
              <FiAlertCircle /> New
            </div>
            <div className="stat-value">{stats.byStatus.new}</div>
          </StatCard>
          <StatCard color="#f57c00">
            <div className="stat-label">
              <FiPhone /> Contacted
            </div>
            <div className="stat-value">{stats.byStatus.contacted}</div>
          </StatCard>
          <StatCard color="#7b1fa2">
            <div className="stat-label">
              <FiClock /> In Progress
            </div>
            <div className="stat-value">{stats.byStatus.inProgress}</div>
          </StatCard>
          <StatCard color="#2e7d32">
            <div className="stat-label">
              <FiCheckCircle /> Converted
            </div>
            <div className="stat-value">{stats.byStatus.converted}</div>
          </StatCard>
          <StatCard color="#f39c12">
            <div className="stat-label">
              <FiCalendar /> Recent (7 days)
            </div>
            <div className="stat-value">{stats.recent}</div>
          </StatCard>
        </Stats>
      )}

      <FilterBar>
        <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="in-progress">In Progress</option>
          <option value="converted">Converted</option>
          <option value="closed">Closed</option>
        </Select>
        
        <SearchInput
          type="text"
          placeholder="Search by name, email, phone, or event type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FilterBar>

      <TableContainer>
        {loading ? (
          <LoadingSpinner>
            <FiRefreshCw />
          </LoadingSpinner>
        ) : filteredEnquiries.length === 0 ? (
          <EmptyState>
            <FiMail />
            <h3>No Enquiries Found</h3>
            <p>There are no enquiries matching your filters.</p>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Contact</Th>
                <Th>Event Type</Th>
                <Th>Preferred Date</Th>
                <Th>Status</Th>
                <Th>Submitted</Th>
              </tr>
            </thead>
            <tbody>
              {filteredEnquiries.map((enquiry) => (
                <Tr key={enquiry._id} onClick={() => openEnquiryDetails(enquiry)}>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FiUser style={{ color: '#999' }} />
                      <strong>{enquiry.firstName} {enquiry.lastName}</strong>
                    </div>
                  </Td>
                  <Td>
                    <div>{enquiry.email}</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>{enquiry.phone}</div>
                  </Td>
                  <Td>{enquiry.eventType || 'Not specified'}</Td>
                  <Td>
                    {enquiry.preferredDate 
                      ? new Date(enquiry.preferredDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                      : 'Not specified'}
                  </Td>
                  <Td>
                    <StatusBadge status={enquiry.status}>
                      {getStatusIcon(enquiry.status)}
                      {enquiry.status.replace('-', ' ').toUpperCase()}
                    </StatusBadge>
                  </Td>
                  <Td style={{ fontSize: '13px', color: '#666' }}>
                    {formatDate(enquiry.createdAt)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>

      <Modal show={showModal} onClick={(e) => e.target === e.currentTarget && closeModal()}>
        <ModalContent>
          <ModalHeader>
            <h2>📧 Enquiry Details</h2>
            <CloseButton onClick={closeModal}>×</CloseButton>
          </ModalHeader>

          {selectedEnquiry && (
            <>
              <DetailSection>
                <h3>Customer Information</h3>
                <DetailRow>
                  <div className="label">Name:</div>
                  <div className="value">
                    <strong>{selectedEnquiry.firstName} {selectedEnquiry.lastName}</strong>
                  </div>
                </DetailRow>
                <DetailRow>
                  <div className="label">Email:</div>
                  <div className="value">{selectedEnquiry.email}</div>
                </DetailRow>
                <DetailRow>
                  <div className="label">Phone:</div>
                  <div className="value">{selectedEnquiry.phone}</div>
                </DetailRow>
                <DetailRow>
                  <div className="label">Preferred Contact:</div>
                  <div className="value">{selectedEnquiry.communicationPreference}</div>
                </DetailRow>
              </DetailSection>

              <DetailSection>
                <h3>Event Details</h3>
                <DetailRow>
                  <div className="label">Event Type:</div>
                  <div className="value">{selectedEnquiry.eventType || 'Not specified'}</div>
                </DetailRow>
                <DetailRow>
                  <div className="label">Preferred Date:</div>
                  <div className="value">
                    {selectedEnquiry.preferredDate 
                      ? new Date(selectedEnquiry.preferredDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                      : 'Not specified'}
                  </div>
                </DetailRow>
                <DetailRow>
                  <div className="label">Available Time:</div>
                  <div className="value">{selectedEnquiry.availableTime || 'Not specified'}</div>
                </DetailRow>
              </DetailSection>

              <DetailSection>
                <h3>Message</h3>
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>
                  {selectedEnquiry.message}
                </div>
              </DetailSection>

              <DetailSection>
                <h3>Status & Notes</h3>
                <DetailRow>
                  <div className="label">Current Status:</div>
                  <div className="value">
                    <StatusBadge status={selectedEnquiry.status}>
                      {getStatusIcon(selectedEnquiry.status)}
                      {selectedEnquiry.status.replace('-', ' ').toUpperCase()}
                    </StatusBadge>
                  </div>
                </DetailRow>
                <DetailRow>
                  <div className="label">Update Status:</div>
                  <div className="value">
                    <Select 
                      value={newStatus} 
                      onChange={(e) => setNewStatus(e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="in-progress">In Progress</option>
                      <option value="converted">Converted</option>
                      <option value="closed">Closed</option>
                    </Select>
                  </div>
                </DetailRow>
                <DetailRow style={{ flexDirection: 'column', gap: '10px' }}>
                  <div className="label">Notes:</div>
                  <TextArea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this enquiry..."
                  />
                </DetailRow>
              </DetailSection>

              <DetailSection>
                <h3>Metadata</h3>
                <DetailRow>
                  <div className="label">Submitted:</div>
                  <div className="value">{formatDate(selectedEnquiry.createdAt)}</div>
                </DetailRow>
                <DetailRow>
                  <div className="label">Last Updated:</div>
                  <div className="value">{formatDate(selectedEnquiry.updatedAt)}</div>
                </DetailRow>
                <DetailRow>
                  <div className="label">IP Address:</div>
                  <div className="value" style={{ fontSize: '12px', color: '#999' }}>
                    {selectedEnquiry.ipAddress}
                  </div>
                </DetailRow>
              </DetailSection>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <Button 
                  primary 
                  onClick={updateEnquiry} 
                  disabled={updateLoading}
                  style={{ flex: 1 }}
                >
                  {updateLoading ? 'Updating...' : 'Update Enquiry'}
                </Button>
                <Button 
                  onClick={() => deleteEnquiry(selectedEnquiry._id)}
                  style={{ background: '#e74c3c', color: 'white', border: 'none' }}
                >
                  Delete
                </Button>
              </div>
            </>
          )}
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default EnquiryManagement;
